// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * PipelineSelector Component
 *
 * Modular dropdown component for selecting, cloning, and creating pipelines.
 * Keeps WorkflowEditor clean by encapsulating all pipeline management UI.
 */

import { useState } from 'react'
import { Button } from '@/components/ui'
import { Copy, Plus, Trash2, ChevronDown, Loader2, Link2, Pencil } from 'lucide-react'
import type { Pipeline } from '../types'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui'
import { CreatePipelineDialog, type CreatePipelineData } from './CreatePipelineDialog'
import { ClonePipelineDialog, type ClonePipelineData } from './ClonePipelineDialog'
import { EditPipelineDialog, type EditPipelineData } from './EditPipelineDialog'

/** Pipeline entry rendered in the dropdown */
interface PipelineEntry {
  id: string
  name: string
  description?: string | null
  /** True when this pipeline inherits from a parent (system or app) */
  inherited: boolean
  /** Source workspace id for app-tier inheritance, parsed from `inherited` */
  sourceWs?: string
}

/** Parse "app.<sourceWsId>" into the source workspace id, "system" → "system". */
function parseInheritedSource(inherited: string | null | undefined): string | undefined {
  if (!inherited) return undefined
  if (inherited === 'system') return 'system'
  if (inherited.startsWith('app.')) return inherited.slice('app.'.length)
  return undefined
}

interface PipelineSelectorProps {
  pipelines: Record<string, Pipeline>
  activePipelineId: string | null
  onSelect: (pipelineId: string) => void
  onClone: (sourcePipelineId: string, data: ClonePipelineData) => void
  onCreate: (data?: CreatePipelineData) => void
  onEdit: (pipelineId: string, data: EditPipelineData) => void
  onDelete: (pipelineId: string) => void
  loading?: boolean
  disabled?: boolean
  /** Whether a create operation is in progress */
  isCreating?: boolean
  /** Whether a save/edit operation is in progress */
  isSaving?: boolean
  /** Whether the active pipeline is a binding (read-only) */
  isActiveBinding?: boolean
  /** False for an agent's single pipeline: hides clone/create/delete and
   * renders the pipeline name as a title instead of a dropdown. */
  canManage?: boolean
}

export function PipelineSelector({
  pipelines,
  activePipelineId,
  onSelect,
  onClone,
  onCreate,
  onEdit,
  onDelete,
  loading = false,
  disabled = false,
  isCreating = false,
  isSaving = false,
  isActiveBinding = false,
  canManage = true,
}: PipelineSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

  const handleCreatePipeline = (data: CreatePipelineData) => {
    onCreate(data)
    setIsCreateDialogOpen(false)
  }

  const handleClonePipeline = (data: ClonePipelineData) => {
    if (activePipelineId) {
      onClone(activePipelineId, data)
      setIsCloneDialogOpen(false)
    }
  }

  const handleEditPipeline = (data: EditPipelineData) => {
    if (activePipelineId) {
      onEdit(activePipelineId, data)
      setIsEditDialogOpen(false)
    }
  }

  const activePipeline = activePipelineId ? pipelines[activePipelineId] : null

  const pipelineEntries: PipelineEntry[] = Object.values(pipelines).map((p): PipelineEntry => ({
    id: p.id,
    name: p.name,
    description: p.description,
    inherited: Boolean(p.inherited),
    sourceWs: parseInheritedSource(p.inherited),
  }))

  const activeEntry = pipelineEntries.find(e => e.id === activePipelineId)

  if (loading) {
    return (
      <div className="flex items-center gap-2 bg-white shadow-sm rounded-lg px-3 py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-gray-600">Loading pipelines...</span>
      </div>
    )
  }

  if (pipelineEntries.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">No pipelines</span>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          size="sm"
          variant="default"
          disabled={disabled}
        >
          <Plus className="h-4 w-4 mr-1" />
          Create Pipeline
        </Button>
        <CreatePipelineDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onCreate={handleCreatePipeline}
          isPending={isCreating}
        />
      </div>
    )
  }

  // An agent owns exactly one pipeline — show its name as a title, no dropdown.
  if (!canManage) {
    const entry = activeEntry ?? pipelineEntries[0]
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium px-2" data-testid="builder-pipeline-title">
          {entry.name}
        </span>
        <Button
          onClick={() => setIsEditDialogOpen(true)}
          size="sm"
          variant="outline"
          disabled={disabled || !activePipelineId || isActiveBinding}
          title="Edit pipeline details"
          data-testid="builder-pipeline-edit"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        {activePipeline && (
          <EditPipelineDialog
            pipeline={activePipeline}
            open={isEditDialogOpen}
            onOpenChange={setIsEditDialogOpen}
            onSave={handleEditPipeline}
            isPending={isSaving}
          />
        )}
      </div>
    )
  }

  // Count non-binding pipelines for delete logic
  const inlinePipelineCount = Object.keys(pipelines).length

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            className="min-w-[200px] justify-between"
            data-testid="builder-pipeline-selector"
          >
            <span className="flex items-center gap-1.5 truncate">
              {activeEntry?.inherited && <Link2 className="h-3.5 w-3.5 text-blue-500 shrink-0" />}
              {activeEntry ? activeEntry.name : 'Select Pipeline'}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[280px]">
          <DropdownMenuLabel>Select Pipeline</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {pipelineEntries.map((entry) => (
            <DropdownMenuItem
              key={entry.id}
              onClick={() => {
                onSelect(entry.id)
                setIsOpen(false)
              }}
              className={activePipelineId === entry.id ? 'bg-accent' : ''}
            >
              <div className="flex items-start gap-2 flex-1">
                {entry.inherited && (
                  <Link2 className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                )}
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="font-medium truncate">{entry.name}</span>
                  {entry.description && (
                    <span className="text-xs text-gray-500 truncate">
                      {entry.description}
                    </span>
                  )}
                  {entry.inherited && entry.sourceWs && (
                    <span className="text-xs text-blue-700 truncate">
                      Linked from {entry.sourceWs}
                    </span>
                  )}
                </div>
              </div>
              {activePipelineId === entry.id && (
                <span className="ml-2 text-primary shrink-0">✓</span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        onClick={() => setIsEditDialogOpen(true)}
        size="sm"
        variant="outline"
        disabled={disabled || !activePipelineId || isActiveBinding}
        title={isActiveBinding ? "Cannot edit linked pipeline" : "Edit pipeline details"}
        data-testid="builder-pipeline-edit"
      >
        <Pencil className="h-4 w-4" />
      </Button>

      {canManage && (
        <Button
          onClick={() => setIsCloneDialogOpen(true)}
          size="sm"
          variant="outline"
          disabled={disabled || !activePipelineId}
          title="Clone current pipeline"
          data-testid="builder-pipeline-clone"
        >
          <Copy className="h-4 w-4" />
        </Button>
      )}

      {canManage && (
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          size="sm"
          variant="outline"
          disabled={disabled}
          title="Create new pipeline"
          data-testid="builder-pipeline-create"
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}

      {canManage && activePipelineId && inlinePipelineCount > 1 && (
        <Button
          onClick={() => onDelete(activePipelineId)}
          size="sm"
          variant="outline"
          disabled={disabled}
          title={
            pipelines[activePipelineId]?.inherited
              ? "Remove this linked pipeline from the workspace"
              : "Delete current pipeline"
          }
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid="builder-pipeline-delete"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}

      <CreatePipelineDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onCreate={handleCreatePipeline}
        isPending={isCreating}
      />

      {activePipeline && (
        <ClonePipelineDialog
          open={isCloneDialogOpen}
          onOpenChange={setIsCloneDialogOpen}
          onClone={handleClonePipeline}
          isPending={isSaving}
          sourceName={activePipeline.name}
          sourceId={activePipeline.id}
          existingIds={new Set(Object.keys(pipelines))}
        />
      )}

      {activePipeline && (
        <EditPipelineDialog
          pipeline={activePipeline}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSave={handleEditPipeline}
          isPending={isSaving}
        />
      )}
    </div>
  )
}
